import type {
  CreateActionTypePayload,
  CreateActionTypeResponse,
} from '@/domains/actionType/actionType.entity';
import type { ActionTypeRepository } from '@/domains/actionType/actionType.repository';

/**
 * Use Case para crear un nuevo tipo de acción en el sistema.
 *
 * Funcionalidad:
 * - Registra nuevos tipos de acciones académicas disponibles en el sistema
 * - Permite ampliar el catálogo de acciones que se pueden aplicar a estudiantes en riesgo
 * - Persiste el tipo de acción mediante el repositorio
 *
 * Responsabilidades:
 * - Validar estructura del payload antes de crear el tipo de acción
 * - Coordinar la creación con el repositorio de tipos de acción
 * - Actuar como intermediario entre dominio e infraestructura
 * - Asegurar que el nuevo tipo de acción cumpla con las reglas de negocio
 */
export class CreateActionTypeUseCase {
  private repository: ActionTypeRepository;

  /**
   * Inicializa el UseCase con las dependencias necesarias.
   *
   * @param repository - Repositorio que implementa ActionTypeRepository para operaciones CRUD
   */
  constructor(repository: ActionTypeRepository) {
    this.repository = repository;
  }

  /**
   * Ejecuta la creación de un nuevo tipo de acción.
   *
   * @param payload - Datos del tipo de acción a crear (descripción, estado, etc.)
   */
  async execute(
    payload: CreateActionTypePayload,
  ): Promise<CreateActionTypeResponse> {
    return await this.repository.createActionType(payload);
  }
}
